import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Timer, SlidersHorizontal, CaretRight, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiError, STATUS_META } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";

function RulesDialog({ open, onClose, onSaved, canEdit }) {
  const [vals, setVals] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/settings/delay-thresholds").then((r) => setVals(r.data.values)); }, [open]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/delay-thresholds", { values: Object.fromEntries(Object.entries(vals).map(([k, v]) => [k, Number(v) || 0])) });
      toast.success("Delay rules saved"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-0 gap-0" data-testid="delay-rules-dialog">
        <DialogHeader className="p-5 border-b border-slate-200 text-left">
          <DialogTitle>Delay rules</DialogTitle>
          <DialogDescription>Maximum days a file may stay on each step. Set 0 to turn off the alert for that step. A daily list is sent at 10:00 AM.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto px-5 py-2 divide-y divide-slate-100">
          {vals && Object.entries(vals).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3 py-2.5">
              <span className="flex-1 text-sm font-medium text-slate-700">{STATUS_META[k]?.label || k}</span>
              <input type="number" min="0" max="365" value={v} disabled={!canEdit} data-testid={`delay-input-${k}`}
                onChange={(e) => setVals((s) => ({ ...s, [k]: e.target.value }))}
                className="w-16 h-9 text-center font-mono font-bold text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-slate-50" />
              <span className="text-xs text-slate-400 w-7">days</span>
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="p-4 border-t border-slate-200">
            <Button className="w-full h-11 rounded-xl" disabled={busy || !vals} onClick={save} data-testid="delay-rules-save">Save rules</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DelayedFiles({ canEdit }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/dashboard/stuck-files").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (rows === null) return null;
  return (
    <section className="rounded-2xl bg-white border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="delayed-files">
      <div className="flex items-center justify-between px-4 sm:px-5 pt-4">
        <h3 className="font-heading font-bold text-[15px] text-slate-900 flex items-center gap-2"><Timer size={18} weight="duotone" className="text-rose-500" /> Delayed files</h3>
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-mono font-bold rounded-full px-2.5 py-1 ${rows.length ? "bg-rose-50 text-rose-700" : "bg-brand-50 text-brand-700"}`} data-testid="delayed-count">{rows.length}</span>
          <button type="button" aria-label="Delay rules" title="Delay rules" data-testid="delay-rules-btn" onClick={() => setOpen(true)}
            className="tap w-9 h-9 rounded-full text-slate-500 hover:bg-slate-100 flex items-center justify-center"><SlidersHorizontal size={18} /></button>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-slate-500"><CheckCircle size={28} weight="duotone" className="mx-auto mb-2 text-brand-500" />No file is stuck beyond its time limit.</div>
      ) : (
        <div className="mt-2 divide-y divide-slate-100 max-h-80 overflow-y-auto">
          {rows.map((r) => (
            <Link key={r.id} to={`/customers/${r.id}`} data-testid={`delayed-row-${r.id}`} className="flex items-center gap-3 px-4 sm:px-5 py-3 hover:bg-slate-50 transition-colors">
              <span className="w-11 h-11 rounded-xl bg-rose-50 text-rose-700 ring-1 ring-rose-200 flex flex-col items-center justify-center shrink-0 leading-none">
                <span className="font-heading font-extrabold text-base">{r.days}</span><span className="text-[9px] font-bold">days</span>
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm text-slate-900 truncate">{r.name}</div>
                <div className="text-xs text-slate-500 truncate">{STATUS_META[r.status]?.label} · limit {r.limit}d · <b className="text-rose-600">{r.over_by}d over</b></div>
              </div>
              <CaretRight size={16} className="text-slate-300 shrink-0" />
            </Link>
          ))}
        </div>
      )}
      <div className="h-2" />
      <RulesDialog open={open} onClose={() => setOpen(false)} onSaved={load} canEdit={canEdit} />
    </section>
  );
}
